/**
 * Active Calls List Component
 * Displays list of ongoing video calls with join buttons
 */

import React from 'react';
import { Card, List, Button, Empty, Spin, Typography, Space, Tag } from 'antd';
import {
  VideoCameraOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useActiveMeetings } from '@/hooks/useVideoCall';
import { formatDistanceToNow } from 'date-fns';
import type { VideoCallMeeting } from '@/api/types/videocall.types';

const { Text } = Typography;

interface ActiveCallsListProps {
  onJoinCall?: (meeting: VideoCallMeeting) => void;
}

export const ActiveCallsList: React.FC<ActiveCallsListProps> = ({
  onJoinCall,
}) => {
  const { data: meetings, isLoading } = useActiveMeetings();

  if (isLoading) {
    return (
      <Card title="Active Calls">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!meetings || meetings.length === 0) {
    return (
      <Card title="Active Calls">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No active calls right now"
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <span>Active Calls</span>
          <Tag color="success">{meetings.length} active</Tag>
        </Space>
      }
    >
      <List
        dataSource={meetings}
        renderItem={(meeting) => (
          <List.Item
            actions={[
              <Button
                key="join"
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={() => onJoinCall?.(meeting)}
              >
                Join
              </Button>,
            ]}
          >
            <List.Item.Meta
              avatar={<VideoCameraOutlined style={{ fontSize: 24 }} />}
              title={
                <Space>
                  <Text strong>{meeting.room_name}</Text>
                  <Tag color="green">Live</Tag>
                </Space>
              }
              description={
                <Space direction="vertical" size="small">
                  <Text type="secondary">
                    <UserOutlined /> Started by {meeting.started_by.name}
                  </Text>
                  <Text type="secondary">
                    <ClockCircleOutlined />{' '}
                    {formatDistanceToNow(new Date(meeting.started_at), {
                      addSuffix: true,
                    })}
                  </Text>
                  {meeting.participant_count !== undefined && (
                    <Text type="secondary">
                      {meeting.participant_count} participant(s)
                    </Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
};
